import React, { useEffect, useState } from "react";
import { RMarker, useMap } from "maplibre-react-components";
import type {
  GeoJSONFeature,
  Map as MapLibreMap,
  MapGeoJSONFeature,
} from "maplibre-gl";
import { useSubscriptionStore } from "@/store/useSubscriptionStore";
import { useTariffLayer } from "@/store/mode";
import { isEdaTariffMode, tariffModeToLayer } from "@/types/base";
import {
  getCenter,
  getNumericFeatureProperty,
  isHexFeature,
} from "@/pages/adminPriceEda/helpers";
import cls from "./styles.module.scss";

type MarkerItem = {
  id: string;
  latitude: number;
  longitude: number;
  label: string;
  count: number;
};

type HexFeature = {
  id: number;
  longitude: number;
  latitude: number;
  price: number;
};

function formatPrice(min: number, max: number) {
  const roundedMax = Math.round(max);
  return `~${roundedMax} ₽/ч`;
}

function getZoomValue(
  zoom: number,
  thresholds: Array<{ minZoom: number; value: number }>,
  fallback: number
) {
  const match = thresholds.find(({ minZoom }) => zoom >= minZoom);
  return match ? match.value : fallback;
}

function getVisibleHexFeatures(
  map: MapLibreMap,
  priceField: string
): HexFeature[] {
  const features = map.queryRenderedFeatures(undefined, {
    layers: ["hex-fill"],
  });
  const deduped = new Map<number, HexFeature>();

  features.forEach((feature: GeoJSONFeature | MapGeoJSONFeature) => {
    if (!isHexFeature(feature)) return;

    const id =
      getNumericFeatureProperty(feature, "id") ??
      getNumericFeatureProperty(feature, "hexgroup_id");
    const price = Number(feature.properties?.[priceField]);
    const center = getCenter(feature);

    if (id === null || !Number.isFinite(price) || !center) return;
    if (deduped.has(id)) return;

    deduped.set(id, {
      id,
      longitude: center[0],
      latitude: center[1],
      price,
    });
  });

  return Array.from(deduped.values());
}

function buildMarkers(
  map: MapLibreMap,
  hexFeatures: HexFeature[]
): MarkerItem[] {
  const zoom = map.getZoom();

  if (zoom >= 14.5) {
    return hexFeatures.map((feature) => ({
      id: String(feature.id),
      longitude: feature.longitude,
      latitude: feature.latitude,
      label: formatPrice(feature.price, feature.price),
      count: 1,
    }));
  }

  const clusterSize = getZoomValue(
    zoom,
    [
      { minZoom: 13.5, value: 88 },
      { minZoom: 12.5, value: 120 },
      { minZoom: 11.5, value: 160 },
    ],
    220
  );
  const grouped = new Map<
    string,
    {
      longitudeSum: number;
      latitudeSum: number;
      minPrice: number;
      maxPrice: number;
      count: number;
      ids: number[];
    }
  >();

  hexFeatures.forEach((feature) => {
    const coordinates: [number, number] = [feature.longitude, feature.latitude];
    const point = map.project(coordinates);
    const key = `${Math.floor(point.x / clusterSize)}:${Math.floor(
      point.y / clusterSize
    )}`;
    const current = grouped.get(key);

    if (!current) {
      grouped.set(key, {
        longitudeSum: feature.longitude,
        latitudeSum: feature.latitude,
        minPrice: feature.price,
        maxPrice: feature.price,
        count: 1,
        ids: [feature.id],
      });
      return;
    }

    current.longitudeSum += feature.longitude;
    current.latitudeSum += feature.latitude;
    current.minPrice = Math.min(current.minPrice, feature.price);
    current.maxPrice = Math.max(current.maxPrice, feature.price);
    current.count += 1;
    current.ids.push(feature.id);
  });

  const mergedMarkers = Array.from(grouped.values())
    .map((group) => ({
      id: group.ids.sort((a, b) => a - b).join("-"),
      longitude: group.longitudeSum / group.count,
      latitude: group.latitudeSum / group.count,
      label: formatPrice(group.minPrice, group.maxPrice),
      count: group.count,
    }))
    .sort((a, b) => b.count - a.count);

  const minDistancePx = getZoomValue(
    zoom,
    [
      { minZoom: 13.5, value: 64 },
      { minZoom: 12.5, value: 84 },
      { minZoom: 11.5, value: 112 },
    ],
    144
  );
  const filteredMarkers: MarkerItem[] = [];

  mergedMarkers.forEach((marker) => {
    const coordinates: [number, number] = [marker.longitude, marker.latitude];
    const point = map.project(coordinates);

    const isTooClose = filteredMarkers.some((existingMarker) => {
      const existingCoordinates: [number, number] = [
        existingMarker.longitude,
        existingMarker.latitude,
      ];
      const existingPoint = map.project(existingCoordinates);
      const dx = point.x - existingPoint.x;
      const dy = point.y - existingPoint.y;

      return Math.sqrt(dx * dx + dy * dy) < minDistancePx;
    });

    if (!isTooClose) {
      filteredMarkers.push(marker);
    }
  });

  const maxMarkers = getZoomValue(
    zoom,
    [
      { minZoom: 13.5, value: 45 },
      { minZoom: 12.5, value: 28 },
      { minZoom: 11.5, value: 18 },
    ],
    10
  );

  return filteredMarkers.slice(0, maxMarkers);
}

const PriceLabelsLayer = () => {
  const map = useMap() as MapLibreMap | null;
  const { isSubscriptionActive } = useSubscriptionStore();
  const tariffLayer = useTariffLayer((s) => s.tariffLayer);
  const isEdaMode = isEdaTariffMode(tariffLayer);
  const [markers, setMarkers] = useState<MarkerItem[]>([]);

  useEffect(() => {
    if (!map || !isSubscriptionActive || !isEdaMode) {
      setMarkers([]);
      return;
    }

    const updateMarkers = () => {
      if (!map.isStyleLoaded() || !map.getLayer("hex-fill")) {
        setMarkers([]);
        return;
      }

      const priceField = tariffModeToLayer[tariffLayer];
      const visibleFeatures = getVisibleHexFeatures(map, priceField);
      setMarkers(buildMarkers(map, visibleFeatures));
    };

    map.on("load", updateMarkers);
    map.on("idle", updateMarkers);
    map.on("moveend", updateMarkers);
    map.on("zoomend", updateMarkers);

    updateMarkers();

    return () => {
      map.off("load", updateMarkers);
      map.off("idle", updateMarkers);
      map.off("moveend", updateMarkers);
      map.off("zoomend", updateMarkers);
    };
  }, [isEdaMode, isSubscriptionActive, map, tariffLayer]);

  if (!isSubscriptionActive || !map || !isEdaMode) return null;

  return (
    <>
      {markers.map((marker) => (
        <RMarker
          key={marker.id}
          longitude={marker.longitude}
          latitude={marker.latitude}
          initialAnchor="center"
        >
          <div
            className={cls.priceMarker}
            data-merged={marker.count > 1 ? "true" : "false"}
          >
            {marker.label}
          </div>
        </RMarker>
      ))}
    </>
  );
};

export default PriceLabelsLayer;
